import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import { Notify } from 'quasar-framework/dist/quasar.mat.esm';
import * as utils from 'core/utils';
import Cleave from 'cleave.js';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

/**
 * Based on QInput component. Provides additional filtering of user input.
 */
@WithRender
@Component
export class MobileInputComponent extends Vue {
    @Prop()
    autocomplete: string;

    @Prop()
    name: string;

    @Prop()
    id: string;

    @Prop()
    error: boolean;

    @Prop()
    stackLabel: string;

    @Prop()
    floatLabel: string;

    @Prop()
    help: boolean;

    @Prop([String, Number])
    maxLength: number;

    @Prop()
    noPassToggle: boolean;

    @Prop()
    placeholder: string;

    @Prop()
    readonly: boolean;

    @Prop({ default: 'text' })
    type: string;

    @Prop()
    typeMode: string;

    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    //@ts-ignore
    @Prop({ type: null }) // WTF?! Always check type if not null.
    value: any;

    @Watch('value')
    onValueChanged(val: any) {
        this.valueShadow = this.parseValue(val);
    }

    valueShadow: string = this.parseValue(this.value);

    cleave: any;

    focused: boolean = false;

    showPass: boolean = false;

    lastNotify: any = undefined; // Force non-reactive

    get inputPlaceholder(): string {
        if (!this.floatLabel || this.labelIsAbove) {
            return this.placeholder;
        } else {
            return '';
        }
    }

    get inputType(): string {
        switch (this.type) {
            case 'password':
                return this.showPass ? 'text' : 'password';
            case 'cardholder':
            case 'document':
            case 'name':
                return 'text';
            case 'cardexpiry':
            case 'cardnumber':
            case 'number':
            case 'date':
            case 'phone':
                return 'tel';
            default:
                return this.type;
        }
    }

    get isPassword() {
        return this.type === 'password';
    }

    get labelIsAbove(): boolean {
        return this.focused || this.length > 0;
    }

    get length(): number {
        if (this.value) {
            return ('' + this.value).length;
        } else {
            return 0;
        }
    }

    mounted() {
        if (this.type === 'date') {
            const options = {
                date: true,
                datePattern: ['d', 'm', 'Y'],
                delimiter: '.'
            };
            const format = moment().localeData().longDateFormat('L');

            if (format[0] === 'D') {
                options.datePattern = ['d', 'm', 'Y'];
            } else {
                options.datePattern = ['m', 'd', 'Y'];
            }

            if (format[2] === '.' || format[2] === '/') {
                options.delimiter = format[2];
            }

            this.cleave = new Cleave(this.$refs.input, options);
        } else if (this.type === 'cardexpiry') {
            const options = {
                date: true,
                datePattern: ['m', 'y'],
                delimiter: '/'
            };

            this.cleave = new Cleave(this.$refs.input, options);
        }
    }

    beforeDestroy() {
        if (this.cleave) {
            this.cleave.destroy();
        }
    }

    parseValue(v) {
        if (v) {
            if (this.type === 'cardexpiry') {
                const m = moment(v);

                if (m.isValid()) {
                    return m.format('MM/YY');
                }
            } else if (this.type === 'date') {
                const m = moment(v);

                if (m.isValid()) {
                    return m.format('L');
                }
            } else {
                return v;
            }
        }
        else if (v === 0) {
            return v;
        }

        return '';
    }

    showToast(message: string) {
        if (this.lastNotify && typeof this.lastNotify === 'function') {
            this.lastNotify();
        }

        this.lastNotify = Notify.create({
            classes: 'bg-negative',
            message: message,
            position: 'bottom',
            timeout: 2000
        });
    }

    onTogglePass() {
        this.showPass = !this.showPass;
    }

    onInput(e) {
        if (e.target.value !== this.value) {
            if (this.type === 'name') {
                const allowCyrillic = this.typeMode === 'cyrillic';
                const allowSpaceSymbol = this.name !== 'mname';

                e.target.value = utils.String.normalizePassengerName(
                    e.target.value,
                    allowCyrillic,
                    () => {
                        this.showToast(i18next.t('error_letters_' + (allowCyrillic ? 'only' : 'latin_only')));
                    },
                    allowSpaceSymbol
                );
            } else if (this.type === 'contact') {
                e.target.value = utils.String.disabledCyrillic(
                    e.target.value,
                    () => {
                        this.showToast(i18next.t('error_letters_latin_numbers_symbols_only_no_specifics'));
                    }
                );
            } else if (this.type === 'document') {
                const allowCyrillic = this.typeMode === 'cyrillic';

                e.target.value = utils.String.normalizeDocumentNumber(
                    e.target.value,
                    allowCyrillic,
                    () => {
                        this.showToast(i18next.t('error_letters_' + (allowCyrillic ? 'numbers_only' : 'latin_numbers_only')));
                    }
                );
            } else if (this.type === 'phone') {
                e.target.value = utils.String.normalizePhone(e.target.value);
            } else if (this.type === 'cardholder') {
                e.target.value = utils.String.normalizeCardHolder(
                    e.target.value,
                    () => {
                        this.showToast(i18next.t('error_letters_latin_only'));
                    }
                );
            } else if (this.type === 'number' || this.type === 'cardnumber') {
                e.target.value = utils.String.normalizeNumber(e.target.value);
            }

            let v = e.target.value;

            if (this.type === 'cardexpiry') {
                if (v) {
                    const m = moment(v, 'MM/YY', true);

                    if (m.isValid()) {
                        v = m.endOf('month').format('YYYY-MM-DD'); // Use ISO-format.

                        this.$emit('input', v);
                        this.$emit('change', v);

                        this.valueShadow = e.target.value;
                    }
                } else {
                    this.$emit('input', null);
                    this.$emit('change', null);

                    this.valueShadow = e.target.value;
                }
            } else if (this.type === 'date') {
                if (v) {
                    const m = moment(v, moment().localeData().longDateFormat('L'), true);

                    if (m.isValid()) {
                        v = m.format('YYYY-MM-DD'); // Use ISO-format.

                        this.$emit('input', v);
                        this.$emit('change', v);

                        this.valueShadow = e.target.value;
                    }
                } else {
                    this.$emit('input', null);
                    this.$emit('change', null);

                    this.valueShadow = e.target.value;
                }
            } else {
                this.$emit('input', v);
                this.$emit('change', v);

                this.valueShadow = e.target.value;
            }
        }
    }

    onFocus(e) {
        this.focused = true;
        this.$emit('focus', e);
    }

    onBlur(e) {
        this.focused = false;

        if (this.type === 'phone') {
            const v = this.$options.filters!.phone(e.target.value);

            if (v !== e.target.value) {
                e.target.value = v;

                this.$emit('input', v);
                this.$emit('change', v);
            }
        } else if (this.type === 'cardnumber') {
            const card = new utils.Card(e.target.value, '');

            if (card.numberIsValid) {
                e.target.value = card.formatCardNumber();

                this.$emit('input', e.target.value);
                this.$emit('change', e.target.value);
            }
        } else if (this.type === 'cardexpiry') {
            const m = moment(this.cleave.getISOFormatDate(), 'MM/YY', true);

            if (m.isValid()) {
                const v = m.endOf('month').format('YYYY-MM-DD'); // Use ISO-format.

                this.$emit('input', v);
                this.$emit('change', v);

                this.valueShadow = e.target.value;
            }
        } else if (this.type === 'date') {
            const m = moment(this.cleave.getISOFormatDate(), 'YYYY-MM-DD', true);

            if (m.isValid()) {
                const v = m.format('YYYY-MM-DD'); // Use ISO-format.

                this.$emit('input', v);
                this.$emit('change', v);

                this.valueShadow = e.target.value;
            }
        }

        this.$emit('blur', e);
    }

    onClick() {
        (<any>this.$refs).input.focus();
        this.$emit('click');
    }
}
